import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  Dialog,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import {
  ArrowBack,
  Download,
  Visibility,
  ExpandMore,
  Description
} from '@mui/icons-material';

const DetalleMantenimiento = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mantenimiento, setMantenimiento] = useState(null);
  const [talleres, setTalleres] = useState([]);
  const [evidencias, setEvidencias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingEvidencias, setLoadingEvidencias] = useState(false);
  const [error, setError] = useState(null);

    // Estados para vista previa
  const [modalAbierto, setModalAbierto] = useState(false);
  const [evidenciaActual, setEvidenciaActual] = useState(null);
  const [vistaPreviaCargando, setVistaPreviaCargando] = useState(false);
  const [vistaPreviaData, setVistaPreviaData] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Cargar datos del mantenimiento
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Cargar mantenimiento
        const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/historial/${id}/`);
        if (!response.ok) throw new Error('Mantenimiento no encontrado');
        
        const data = await response.json();
        setMantenimiento(data);
        
        // Cargar talleres
        const talleresResponse = await fetch('https://bei360.injupemp.gob.hn:8444/api/taller/');
        if (talleresResponse.ok) {
          const talleresData = await talleresResponse.json();
          setTalleres(talleresData);
        }
        
        // Cargar evidencias
        await cargarEvidencias(data.id);
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // Cargar evidencias
  const cargarEvidencias = async (historialId) => {
    try {
      setLoadingEvidencias(true);
      const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/evidencias/por-mantenimiento/${historialId}/`);
      if (response.ok) {
        const data = await response.json();
        setEvidencias(data);
      }
    } catch (err) {
      console.error('Error cargando evidencias:', err);
    } finally {
      setLoadingEvidencias(false);
    }
  };

  // Función para obtener vista previa en base64
const obtenerVistaPrevia = async (historialId, evidenciaId) => {
  try {
    setVistaPreviaCargando(true);
    const response = await fetch(
      `https://bei360.injupemp.gob.hn:8444/api/historial/${historialId}/evidencias/${evidenciaId}/vista-previa/`
    );
    
    if (!response.ok) {
      throw new Error('Error al obtener vista previa');
    }
    
    const data = await response.json();
    setVistaPreviaData(data);
    return data;
  } catch (error) {
    console.error('Error obteniendo vista previa:', error);
    mostrarSnackbar('Error al cargar la vista previa', 'error');
    return null;
  } finally {
    setVistaPreviaCargando(false);
  }
};


  // Función para descargar evidencia usando el nuevo endpoint
  const descargarEvidencia = async (mantenimientoId, evidenciaId, nombreArchivo) => {
    try {
      const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/historial/${mantenimientoId}/evidencias/${evidenciaId}/descargar/`);
      
      if (!response.ok) {
        throw new Error('Error al descargar archivo');
      }
      
      // Crear blob y descargar
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = nombreArchivo || 'evidencia';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      mostrarSnackbar('Descarga iniciada', 'success');
    } catch (error) {
      console.error('Error descargando evidencia:', error);
      // Fallback al método anterior
      handleDownloadEvidenciaFallback(evidenciaId, nombreArchivo);
    }
  };

  // Fallback para descarga directa (método anterior)
  const handleDownloadEvidenciaFallback = (evidenciaUrl, nombreArchivo) => {
    try {
      const link = document.createElement('a');
      link.href = evidenciaUrl;
      link.download = nombreArchivo || 'evidencia';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      mostrarSnackbar('Descarga iniciada', 'success');
    } catch (error) {
      console.error('Error en descarga fallback:', error);
      mostrarSnackbar('Error al descargar el archivo', 'error');
    }
  };

  // Abrir modal de vista previa
 const handleAbrirModal = async (evidencia) => {
  setEvidenciaActual(evidencia);
  setModalAbierto(true);
  setVistaPreviaData(null);

  await obtenerVistaPrevia(id, evidencia.id);
};


  // Cerrar modal
  const handleCerrarModal = () => {
    setModalAbierto(false);
    setEvidenciaActual(null);
    setVistaPreviaData(null);
  };



  // Función para obtener nombre del taller
  const getNombreTaller = (tallerId) => {
    if (!tallerId) return 'No asignado';
    const taller = talleres.find(t => t.id === tallerId);
    return taller ? taller.nombre : `Taller #${tallerId}`;
  };

  // Función para formatear fecha
  const formatFecha = (fechaString) => {
    if (!fechaString) return 'No especificada';
    const fecha = new Date(fechaString);
    return fecha.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

   // Mostrar snackbar/notificación
  const mostrarSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const cerrarSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Obtener nombre del archivo
  const obtenerNombreArchivo = (evidencia) => {
    if (evidencia.archivo) {
      return evidencia.archivo.split('/').pop();
    }
    return `evidencia_${evidencia.id}`;
  };

  // Función para descargar evidencia
  const handleDownloadEvidencia = (evidenciaUrl, nombreArchivo) => {
    const link = document.createElement('a');
    link.href = evidenciaUrl;
    link.download = nombreArchivo || 'evidencia';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          onClick={() => navigate('/Flotas/HistorialMantenimientos')}
          variant="contained"
          sx={{ backgroundColor: '#19438C' }}
        >
          Volver al historial
        </Button>
      </Container>
    );
  }

  if (!mantenimiento) {
    return (
      <Container sx={{ mt: 4 }}>
        <Alert severity="warning">
          No se encontró el mantenimiento solicitado
        </Alert>
        <Button 
          onClick={() => navigate('/Flotas/HistorialMantenimientos')}
          variant="contained"
          sx={{ backgroundColor: '#19438C' }}
        >
          Volver al historial
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 3, backgroundColor: '#f0f0f0', marginBottom: '2rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton 
            onClick={() => navigate('/Flotas/HistorialMantenimientos')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h5" gutterBottom sx={{ textAlign: 'center', fontWeight: 'bold', flexGrow: 1 }}>
            Detalles del Mantenimiento #{mantenimiento.id}
          </Typography>
        </Box>

        <TableContainer component={Paper} sx={{ maxWidth: 750, margin: '0 auto', mb: 3 }}>
          <Table>
            <TableBody>
              {/* Vehículo */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '40%', borderRight: '1px solid #e0e0e0' }}>
                  Vehículo:
                </TableCell>
                <TableCell>
                  {mantenimiento.vehiculo_placa || `Vehículo #${mantenimiento.vehiculo}`}
                </TableCell>
              </TableRow>

                {/* Usuario creador */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Creado por:
                </TableCell>
                <TableCell>{mantenimiento.usuario_nombre || 'No especificada'}</TableCell>
              </TableRow>

              {/* Tipo de Mantenimiento */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Tipo de Mantenimiento:
                </TableCell>
                <TableCell>
                  <Chip 
                    label={mantenimiento.tipo_mantenimiento} 
                    color={mantenimiento.tipo_mantenimiento === 'Preventivo' ? 'success' : 'warning'} 
                  />
                </TableCell>
              </TableRow>

              {/* Taller */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Taller:
                </TableCell>
                <TableCell>{getNombreTaller(mantenimiento.taller)}</TableCell>
              </TableRow>

              {/* Fecha de Realización */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Fecha de Realización:
                </TableCell>
                <TableCell>{formatFecha(mantenimiento.fecha_realizacion)}</TableCell>
              </TableRow>

              {/* Descripción */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Descripción:
                </TableCell>
                <TableCell>{mantenimiento.descripcion || 'No especificada'}</TableCell>
              </TableRow>

              {/* Kilometraje Programado */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Kilometraje Programado:
                </TableCell>
                <TableCell>
                  {mantenimiento.kilometraje_programado ? `${mantenimiento.kilometraje_programado} km` : 'N/A'}
                </TableCell>
              </TableRow>

              {/* Kilometraje Real */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Kilometraje Real:
                </TableCell>
                <TableCell>
                  {mantenimiento.kilometraje ? `${mantenimiento.kilometraje} km` : 'No registrado'}
                </TableCell>
              </TableRow>

              {/* Observaciones */}
              {mantenimiento.observaciones && (
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                    Observaciones:
                  </TableCell>
                  <TableCell>{mantenimiento.observaciones}</TableCell>
                </TableRow>
              )}

              {/* Fecha de Creación */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderRight: '1px solid #e0e0e0' }}>
                  Fecha de Registro:
                </TableCell>
                <TableCell>{formatFecha(mantenimiento.fecha_creacion)}</TableCell>
              </TableRow>

                 {/* Accordion de Evidencias */}
              <TableRow>
                <TableCell colSpan={2} sx={{ padding: 0 }}>
                  <Accordion 
                    elevation={0} 
                    sx={{ 
                      '&:before': { display: 'none' }, 
                      backgroundColor: 'transparent' 
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore />}
                      sx={{
                        backgroundColor: '#f5f5f5',
                        borderTop: '1px solid #e0e0e0',
                        minHeight: '48px !important',
                        '& .MuiAccordionSummary-content': { margin: '12px 0' },
                      }}
                    >
                      <Typography sx={{ fontWeight: 'bold' }}>
                        Evidencias del Mantenimiento ({evidencias.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: 0, backgroundColor: '#fafafa', borderTop: '1px solid #e0e0e0' }}>
                      <Box sx={{ p: 2 }}>
                        {loadingEvidencias ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        ) : evidencias.length > 0 ? (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '300px', overflowY: 'auto' }}>
                            {evidencias.map((evidencia) => (
                              <Paper
                                key={evidencia.id}
                                elevation={0}
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 1, 
                                  p: 1, 
                                  '&:hover': { backgroundColor: '#f5f5f5' } 
                                }}
                              >
                                <Description fontSize="small" color="action" />
                                <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                  {obtenerNombreArchivo(evidencia)}
                                </Typography>
                                <Tooltip title="Vista previa">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => handleAbrirModal(evidencia)}
                                  >
                                    <Visibility fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Descargar">
                                  <IconButton 
                                    size="small" 
                                    onClick={() => descargarEvidencia(
                                      id, 
                                      evidencia.id, 
                                      obtenerNombreArchivo(evidencia)
                                    )}
                                  >
                                    <Download fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Paper>
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center', py: 2 }}>
                            No hay evidencias disponibles para este mantenimiento
                          </Typography>
                        )}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Modal de Vista Previa */}
<Dialog 
  open={modalAbierto} 
  onClose={handleCerrarModal}
  maxWidth="md"
  fullWidth
>
  <DialogContent sx={{ position: 'relative', minHeight: '500px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    {vistaPreviaCargando ? (
      <CircularProgress />
    ) : vistaPreviaData ? (
      <Box sx={{ textAlign: 'center', width: '100%' }}>
        {/* Vista previa de imágenes */}
        {vistaPreviaData.tipo && vistaPreviaData.tipo.startsWith('image/') ? (
          <img 
            src={`data:${vistaPreviaData.tipo};base64,${vistaPreviaData.base64}`}
            alt={vistaPreviaData.nombre}
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          />
        ) : vistaPreviaData.tipo === 'application/pdf' ? (
          // Vista previa de PDF
          <iframe
            src={`data:application/pdf;base64,${vistaPreviaData.base64}`}
            title={vistaPreviaData.nombre}
            width="100%"
            height="600px"
            style={{ border: 'none' }}
          />
        ) : (
          // Otros tipos de archivos no soportados
          <Box>
            <Description sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              {vistaPreviaData.nombre}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Este tipo de archivo no tiene vista previa disponible
            </Typography>
            <Button 
              variant="contained" 
              sx={{ mt: 2, backgroundColor: '#19438C' }}
              onClick={() => descargarEvidencia(id, evidenciaActual.id, vistaPreviaData.nombre)}
            >
              Descargar Archivo
            </Button>
          </Box>
        )}
      </Box>
    ) : (
      <Typography color="textSecondary">
        No se pudo cargar la vista previa
      </Typography>
    )}
  </DialogContent>
  <DialogActions>
    <Button color="error" onClick={handleCerrarModal}>Cerrar</Button>
    {vistaPreviaData && (
      <Button 
        onClick={() => descargarEvidencia(id, evidenciaActual.id, vistaPreviaData.nombre)}
        startIcon={<Download />}
        variant="contained"
        sx={{ backgroundColor: '#19438C' }}
      >
        Descargar
      </Button>
    )}
  </DialogActions>
</Dialog>


      {/* Snackbar para notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={cerrarSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={cerrarSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Container>
  );
};

export default DetalleMantenimiento;